import { useState } from 'react';
import { useTranslate } from '../../../../shared/hooks/useTranslate';
import { useForm } from 'react-hook-form';
import { CardElement } from '@stripe/react-stripe-js';
import { getCakeLang } from '../../../../i18n';

const paymentMethod = {
	NONE: 'none',
	CREDIT_CARD: 'credit_card',
	EXTERNAL: 'external',
} as const;

type PaymentMethod = (typeof paymentMethod)[keyof typeof paymentMethod];

export const canSubmit = (
	paymentInProgress: boolean,
	{
		terms,
		name,
		address,
		zip,
		city,
	}: {
		terms: boolean;
		name: string;
		address: string;
		zip: string;
		city: string;
	}
): boolean => {
	return (
		!paymentInProgress &&
		terms &&
		name !== '' &&
		address !== '' &&
		zip !== '' &&
		city !== ''
	);
};

const getInitialValues = (mode: string) => {
	return {
		name: '',
		address: '',
		address_zip: '',
		city: '',
		siret: '',
		number: mode === 'test' ? '4111111111111111' : '',
		exp_month: mode === 'test' ? '02' : '',
		exp_year: mode === 'test' ? '2025' : '',
		cvc: mode === 'test' ? '848' : '',
		terms: false,
	};
};

export type PaymentFormData = ReturnType<typeof getInitialValues>;

interface Props {
	forceCreditCard: boolean;
	mode: string;
	onSubmitCreditCardMethod: (data: PaymentFormData) => void;
	processing: boolean;
	onSubmitExternalPaymentMethod: (formValues: PaymentFormData) => void;
	externalPaymentMethodInformation: string;
}

export const PaymentForm = ({
	forceCreditCard,
	mode,
	onSubmitCreditCardMethod,
	processing,
	onSubmitExternalPaymentMethod,
	externalPaymentMethodInformation,
}: Props) => {
	const { __, lang } = useTranslate();
	const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(
		forceCreditCard ? paymentMethod.CREDIT_CARD : paymentMethod.NONE
	);
	const selectPaymentMethod = (method: PaymentMethod) => {
		setSelectedMethod(method);
	};

	const [type, setType] = useState<'individual' | 'company' | null>(null);

	const testMode = mode === 'test';

	const { register, handleSubmit, watch } = useForm({
		defaultValues: getInitialValues(mode),
	});
	const terms = watch('terms');
	const name = watch('name');
	const address = watch('address');
	const zip = watch('address_zip');
	const city = watch('city');

	const onSubmit = (data: PaymentFormData) => {
		if (selectedMethod === paymentMethod.CREDIT_CARD) {
			onSubmitCreditCardMethod(data);
		} else if (selectedMethod === paymentMethod.EXTERNAL) {
			onSubmitExternalPaymentMethod(data);
		} else {
			throw new Error('Invalid payment method');
		}
	};

	return (
		<div>
			{!forceCreditCard && (
				<>
					<p className="text-center">{__('Choose a payment method')}</p>

					<div className="tw:flex tw:justify-center tw:mb-7.5">
						<div
							role="button"
							className={`payment-method ${
								selectedMethod === paymentMethod.CREDIT_CARD ? 'selected ' : ''
							}`}
							onClick={() => selectPaymentMethod(paymentMethod.CREDIT_CARD)}
						>
							<i className="logo fa fa-credit-card" aria-hidden="true" />
							{__('Online payment')}
						</div>
						<div
							role="button"
							className={`payment-method ${
								selectedMethod === paymentMethod.EXTERNAL ? 'selected ' : ''
							}`}
							onClick={() => selectPaymentMethod(paymentMethod.EXTERNAL)}
						>
							<i className="logo fa fa-question" aria-hidden="true" />
							{__('Other payment method')}
						</div>
					</div>
				</>
			)}

			{selectedMethod !== paymentMethod.NONE && (
				<>
					<p style={{ textAlign: 'center' }}>
						{__('Are you making this purchase as an individual or a company?')}
					</p>
					<div className="tw:flex tw:justify-center tw:mb-7.5">
						<div
							role="button"
							className={`payment-method ${
								type === 'individual' ? 'selected ' : ''
							}`}
							onClick={() => setType('individual')}
						>
							<i className="logo fa fa-user" aria-hidden="true" />
							{__('Individual')}
						</div>
						<div
							role="button"
							className={`payment-method ${type === 'company' ? 'selected ' : ''}`}
							onClick={() => setType('company')}
						>
							<i className="logo fa fa-building" aria-hidden="true" />
							{__('Company')}
						</div>
					</div>
				</>
			)}

			{type !== null && (
				<form onSubmit={handleSubmit(onSubmit)}>
					{selectedMethod === paymentMethod.EXTERNAL &&
						externalPaymentMethodInformation !== '' && (
							<>
								<p
									className="text-center"
									style={{ fontWeight: 'bold', margin: '30px 0' }}
								>
									{__('Follow procedure below')}
								</p>

								<div
									dangerouslySetInnerHTML={{
										__html: externalPaymentMethodInformation,
									}}
								/>
							</>
						)}

					<div style={{ textAlign: 'center', marginBottom: 35 }}>
						<p>
							<span style={{ fontWeight: 'bold' }}>
								{__('Fill in your informations')}
							</span>
							<br />
							<span style={{ color: '#737373', fontSize: '14px' }}>
								{__('These informations will appear in the invoice')}
							</span>
						</p>
					</div>

					{type === 'individual' ? (
						<>
							<div className="form-group">
								<label htmlFor="name">
									{__('Full name')} <span className="required">*</span>
								</label>
								<input
									type="text"
									id="name"
									className="form-control"
									{...register('name')}
								/>
							</div>
						</>
					) : type === 'company' ? (
						<>
							<div className="form-group">
								<label htmlFor="name">
									{__('Company name')} <span className="required">*</span>
								</label>
								<input
									type="text"
									id="name"
									className="form-control"
									{...register('name')}
								/>
							</div>
							<div className="form-group">
								<label htmlFor="siret">{__('SIRET number')} </label>
								<input
									type="text"
									id="siret"
									className="form-control"
									{...register('siret')}
								/>
							</div>
						</>
					) : null}

					<div className="form-group">
						<label htmlFor="address">
							{__('Address')} <span className="required">*</span>
						</label>
						<input
							type="text"
							id="address"
							className="form-control"
							{...register('address')}
						/>
					</div>

					<div className="row">
						<div className="col-md-6">
							<div className="form-group">
								<label htmlFor="address_zip">
									{__('ZIP code')} <span className="required">*</span>
								</label>
								<input
									type="text"
									id="address_zip"
									className="form-control"
									{...register('address_zip')}
								/>
							</div>
						</div>

						<div className="col-md-6">
							<div className="form-group">
								<label htmlFor="city">
									{__('City')} <span className="required">*</span>
								</label>
								<input
									type="text"
									id="city"
									className="form-control"
									{...register('city')}
								/>
							</div>
						</div>
					</div>

					{selectedMethod === paymentMethod.CREDIT_CARD && (
						<div className="form-group">
							<label htmlFor="number">
								{__('Card Number')} <span className="required">*</span>
							</label>
							<CardElement
								className="form-control"
								options={{
									style: {
										base: {
											fontSize: '16px',
											color: 'rgb(85, 85, 85)',
											fontFamily: '"Source Sans Pro", sans-serif',
										},
									},
									hidePostalCode: true,
								}}
							/>
							{testMode && (
								<div
									className="alert alert-warning"
									style={{ marginTop: '10px' }}
									role="alert"
								>
									<p>
										{__(
											'You can test different scenarios using the following card numbers :'
										)}
									</p>
									<ul>
										<li>
											<span style={{ userSelect: 'all' }}>
												4000 0000 0000 3220
											</span>{' '}
											{__('to test 3D Secure 2 payments')}
										</li>
										<li>
											<span style={{ userSelect: 'all' }}>
												4000 0000 0000 3063
											</span>{' '}
											{__('to test 3D Secure payments')}
										</li>
										<li>
											<span style={{ userSelect: 'all' }}>
												4242 4242 4242 4242
											</span>{' '}
											{__('to test simple payments')}
										</li>
									</ul>
									<p>
										{__(
											'You can use anything you want for the expiration date and cvc number.'
										)}
									</p>
								</div>
							)}
						</div>
					)}

					<div className="form-group">
						<div className="checkbox">
							<label htmlFor="terms">
								<input type="checkbox" id="terms" {...register('terms')} />
								{__(
									'Click here to indicate that you have read and agree to the terms presented in the'
								)}{' '}
								<a
									href={`/files/legal/tos_ticketing_${getCakeLang(lang)}.pdf`}
									target="_blank"
									rel="noopener noreferrer"
								>
									{__('terms of use')}
								</a>
								.<span className="required">*</span>
							</label>
						</div>
					</div>

					<div style={{ fontStyle: 'italic' }}>
						<span className="required">*</span> {__('Required field')}
					</div>

					<div style={{ textAlign: 'center' }}>
						<button
							type="submit"
							className={`btn btn-ticketing-2 btn-primary ${
								processing ? 'btn-loading' : ''
							}`}
							disabled={
								!canSubmit(processing, {
									terms,
									name,
									address,
									zip,
									city,
								})
							}
						>
							{selectedMethod === paymentMethod.CREDIT_CARD
								? __('Pay order')
								: __('Complete order')}
						</button>
					</div>
				</form>
			)}
		</div>
	);
};
